import React from "react";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Filter, ArrowDownAZ, ArrowUpAZ, Search, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel,
  DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const SORT_FIELDS = ["stage", "title", "value", "probability", "expected_spk_date", "expected_completion_date"];

export const PipelineToolbar = ({
  visibleStages, onToggleStage, onSelectAllStages, onClearStages,
  sortField, sortDir, onSortField, onToggleSort,
  query, onQuery, picFilter, onPicFilter, picOptions,
  canWrite, canAdd, onAdd, onReset,
}) => {
  const { t } = useLang();
  const cfg = useConfig();
  const stages = cfg?.activeStages || [];
  const allSelected = visibleStages.size === stages.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <Input
          data-testid="pipeline-search-input"
          placeholder={t("search_deals")}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          className="pl-9 w-52 h-9 rounded-full bg-white border-slate-200"
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" data-testid="stage-filter-menu" className="h-9 gap-2 rounded-full border-slate-200 bg-white text-slate-700">
            <Filter className="w-4 h-4" />
            {t("stage")}: {allSelected ? t("all") : (visibleStages.size || t("none"))}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs">{t("show_stages")}</DropdownMenuLabel>
          {stages.map((s) => (
            <DropdownMenuCheckboxItem
              key={s.key}
              data-testid={`stage-filter-option-${s.key}`}
              checked={visibleStages.has(s.key)}
              onCheckedChange={() => onToggleStage(s.key)}
              onSelect={(e) => e.preventDefault()}
            >
              <span className={`stage-dot ${cfg.stageStyle(s.key).dot} mr-2`}></span>{cfg.stageLabel(s.key)}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="stage-filter-select-all" onSelect={(e) => { e.preventDefault(); onSelectAllStages(); }}>
            {t("select_all_stages")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="stage-filter-clear" onSelect={(e) => { e.preventDefault(); onClearStages(); }}>
            {t("clear_stages")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Select value={picFilter} onValueChange={onPicFilter}>
        <SelectTrigger data-testid="pic-filter-select" className="h-9 w-40 rounded-full bg-white border-slate-200 text-slate-700">
          <SelectValue placeholder={t("all_pics")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("all_pics")}</SelectItem>
          {picOptions.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={sortField} onValueChange={onSortField}>
        <SelectTrigger data-testid="sort-field-select" className="h-9 w-44 rounded-full bg-white border-slate-200 text-slate-700">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SORT_FIELDS.map((f) => <SelectItem key={f} value={f}>{t("sort_by")}: {t(f === "stage" ? "stage" : f)}</SelectItem>)}
        </SelectContent>
      </Select>

      <Button variant="outline" onClick={onToggleSort} data-testid="stage-sort-toggle"
        className="h-9 gap-2 rounded-full border-slate-200 bg-white text-slate-700">
        {sortDir === "asc" ? <ArrowDownAZ className="w-4 h-4" /> : <ArrowUpAZ className="w-4 h-4" />}
        {sortDir === "asc" ? t("ascending") : t("descending")}
      </Button>

      <Button variant="ghost" onClick={onReset} data-testid="reset-filters-button" className="h-9 gap-1.5 rounded-full text-slate-500">
        <RotateCcw className="w-3.5 h-3.5" /> {t("reset_filters")}
      </Button>

      {canWrite && (
        <Button
          onClick={onAdd}
          data-testid="add-deal-button"
          disabled={!canAdd}
          className="h-9 rounded-full bg-orange-500 hover:bg-orange-600 gap-2 shadow-[0_6px_16px_rgba(234,88,12,0.28)]"
        ><Plus className="w-4 h-4" /> {t("add_deal")}</Button>
      )}
    </div>
  );
};

export default PipelineToolbar;
